import { useCallback, useMemo } from "react";
import useSWR from "swr";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useHasCompany } from "../../../../../utils/hooks/company";

export const useMultiprofileData = ({ isMyOvertime, params = {} }) => {
    const hasCompany = useHasCompany();
    const { groups } = useGroups();

    const url = useMemo(() => {
        if (isMyOvertime) {
            return "/time-sheet/my-multiple-profiles";
        }
        return "/time-sheet/multiple-profiles";
    }, [isMyOvertime]);

    const { data, isLoading } = useSWR(hasCompany
        ? { url, params }
        : null,
        { fallbackData: { data: { result: [], totalItems: 0 } } }
    );

    const uniqueById = (items) => {
        const map = new Map();
        items.forEach(item => {
            if (item == null) return;
            // Objects → use id
            if (typeof item === "object") {
                if (!map.has(item.id)) {
                    map.set(item.id, item);
                }
            }
            // Primitives → use value
            else {
                map.set(item, item);
            }
        });
        return Array.from(map.values());
    };

    const withProfiles = useCallback((main, profiles, key) => {
        return uniqueById([main, ...profiles.map(p => p[key])])
    }, []);

    const formattedData = useMemo(() => {
        return data.result?.map(({ otherProfiles = [], ...item }) => ({
            ...item,
            employeeNumbers: withProfiles(item.employeeNumber, otherProfiles, "employeeNumber"),
            companies: withProfiles(item.primaryCompany, otherProfiles, "primaryCompany"),
            payGroups: withProfiles(item.payGroup, otherProfiles, "payGroup"),
            calculationGroups: withProfiles(item.calculationGroup, otherProfiles, "calculationGroup"),
            ...(groups?.reduce((acc, group) => {
                if (!group?.level) return acc;
                const key = group.level.replaceAll("_", "");
                return {
                    ...acc,
                    [group.level]: withProfiles(item?.[key], otherProfiles, key),
                };
            }, {})),
            ids: [item.id, ...otherProfiles.map((profile) => profile.id)],
            otherProfiles
        }));
    }, [data.result, groups, withProfiles]);

    return {
        data: formattedData,
        isLoading,
        totalItems: data?.totalItems || 0
    }
}
